import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenService } from '@/pages/auth/token.service';

/**
 * Browser side of the payload-encryption layer (AES-256-GCM via the Web Crypto
 * API). After login it performs a one-time handshake to fetch a per-session key;
 * if the backend has encryption disabled it simply gets none and the app stays
 * on plain HTTPS. The interceptor uses {@link encrypt}/{@link decrypt} per call.
 *
 * Web Crypto requires a secure context (HTTPS, or localhost in dev).
 */
@Injectable({ providedIn: 'root' })
export class CryptoService {
  private key: CryptoKey | null = null;
  private session: string | null = null;
  private bootstrapping: Promise<void> | null = null;
  /** In-flight stale-key recovery, shared by all concurrent 428 retries. */
  private rehandshaking: Promise<void> | null = null;
  /** Set once the backend tells us encryption is off, so we stop re-asking. */
  private disabled = false;

  constructor(private http: HttpClient, private tokens: TokenService) {}

  /** True once a usable session key is held. */
  get active(): boolean { return !!this.key && !!this.session; }
  get sessionId(): string | null { return this.session; }

  /** Drop the key (logout, or a stale session after a backend restart). */
  reset(): void { this.key = null; this.session = null; this.bootstrapping = null; this.disabled = false; }

  /**
   * The backend answered 428/X-Enc-Stale — it restarted and lost our session key.
   * Drop the stale key and fetch a fresh one right away so the failed call can be
   * replayed. Concurrent callers (a restart fails every in-flight call at once)
   * share a single handshake so they can't reset each other's fresh key mid-use.
   * Returns whether encryption is active again (false = the backend now has the
   * layer disabled, so callers should retry on plain HTTPS).
   */
  async rehandshake(): Promise<boolean> {
    if (!this.rehandshaking) {
      this.reset();
      this.rehandshaking = this.handshake()
        .catch(() => { /* backend unreachable — stay plain for now */ })
        .finally(() => { this.rehandshaking = null; });
    }
    await this.rehandshaking;
    return this.active;
  }

  /** Fetch a session key once — no-op if disabled, not logged in, or already done/in-flight. */
  maybeBootstrap(): void {
    if (this.active || this.disabled || this.bootstrapping) return;
    if (!this.tokens.isLoggedIn()) return;
    this.bootstrapping = this.handshake().catch(() => { /* stay on plain HTTPS */ });
  }

  private async handshake(): Promise<void> {
    const res: any = await firstValueFrom(
      this.http.post(`${environment.apiBaseUrl}/api/crypto/handshake`, {})
    );
    if (!res?.enabled || !res?.key || !res?.sessionId) { this.disabled = true; return; }
    const raw = this.b64ToBytes(res.key);
    this.key = await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    this.session = res.sessionId;
  }

  /** Raw bytes → raw iv[12] || ciphertext||tag (used for binary file payloads). */
  async encryptBytes(data: Uint8Array): Promise<Uint8Array> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, this.key!, data);
    const ctBytes = new Uint8Array(ct);
    const out = new Uint8Array(iv.length + ctBytes.length);
    out.set(iv, 0);
    out.set(ctBytes, iv.length);
    return out;
  }

  /** base64( iv||ct ) → raw plaintext bytes (used to decrypt downloaded files). */
  async decryptToBytes(base64: string): Promise<Uint8Array> {
    const blob = this.b64ToBytes(base64);
    const iv = blob.slice(0, 12);
    const ct = blob.slice(12);
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, this.key!, ct);
    return new Uint8Array(pt);
  }

  /** plaintext text → base64( iv[12] || ciphertext||tag ). */
  async encrypt(text: string): Promise<string> {
    return this.bytesToB64(await this.encryptBytes(new TextEncoder().encode(text)));
  }

  /** Inverse of {@link encrypt}. */
  async decrypt(base64: string): Promise<string> {
    return new TextDecoder().decode(await this.decryptToBytes(base64));
  }

  private bytesToB64(b: Uint8Array): string {
    let s = '';
    for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
    return btoa(s);
  }

  private b64ToBytes(s: string): Uint8Array {
    const bin = atob(s);
    const b = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
    return b;
  }
}
